import { inject, Injectable, OnDestroy } from '@angular/core';
import { defer, distinctUntilChanged, filter, firstValueFrom, map, merge, Observable, skip, Subject, Subscription, takeUntil, timeout } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { AudioCallAttemptFinishRequest, AudioCallStartCancelRequest, AvatarOrderDataResponse, Client } from '@api/client';
import { CALL_ORDER_STATUS_NUMBERS, CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { AuthService } from '@services/auth.service';
import { CallOrderUpdatesService } from '@app/services/call-order-updates.service';
import { AudioCallTelemetryService } from '@app/services/audio-call-telemetry.service';
import { isAudioCallAttemptId } from '@app/services/audio-call-attempt';
import { CallSessionService } from '@call/services/call-session.service';
import { PlatformService } from '@services/platform.service';
import { OnlineStateService } from '@services/online-state.service';
import { AppStateChangeService } from '@services/app-state-change.service';

interface CallTerminationIdentity {
  userId: number;
  attemptId: string;
  rejected?: true;
  retryAfter?: number;
}

interface CallExitIntent extends CallTerminationIdentity {
  orderId: number;
}

interface CallStartCancelIntent extends CallTerminationIdentity {
  toUserId: number;
}

type CallTerminationIntent = CallExitIntent | CallStartCancelIntent;

interface RetainedCallExit {
  raw: string;
  intent: CallTerminationIntent;
  batches: number;
  authBlockedVersion?: number;
  restored: boolean;
  storageWrite: Promise<void>;
}

@Injectable({providedIn: 'root'})
export class AudioCallFinalizationService implements OnDestroy {
  private readonly client: Client = inject(Client);
  private readonly authService: AuthService = inject(AuthService);
  private readonly updates: CallOrderUpdatesService = inject(CallOrderUpdatesService);
  private readonly telemetry: AudioCallTelemetryService = inject(AudioCallTelemetryService);
  private readonly session: CallSessionService = inject(CallSessionService);
  private readonly platform: PlatformService = inject(PlatformService);
  private readonly online: OnlineStateService = inject(OnlineStateService);
  private readonly appState: AppStateChangeService = inject(AppStateChangeService);
  private static readonly EXIT_INTENT_PREFIX = 'audioCallExitIntent.';
  private readonly pending: Map<string, Promise<boolean>> = new Map();
  private readonly retained = new Map<string, RetainedCallExit>();
  private readonly subscriptions = new Subscription();
  private readonly destroyed$ = new Subject<void>();
  private readonly maxAttempts: number = 3;
  private readonly maxBatches: number = 3;
  private readonly requestTimeoutMs: number = 5000;
  private destroyed = false;
  private recoveryScheduled = false;
  private recoveryRunning = false;
  private recoveryRequested = false;
  private readonly pageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) return;
    const order = this.session.selectedOrder;
    const userId = this.authService.parsedToken?.userId;
    if (!order || !userId || order.myUserId !== userId || !order.id) return;
    const attemptId = this.session.getAttempt(order.id, userId);
    if (!attemptId) return;
    this.retain({orderId: order.id, userId, attemptId});
  };

  constructor() {
    if (!this.platform.isBrowser) return;
    if (this.platform.isDevice) void this.restoreNativeIntents();
    else {
      this.readExitIntents();
      window.addEventListener('pagehide', this.pageHide);
    }
    this.subscriptions.add(this.authService.parsedToken$.pipe(
      map(token => `${token?.userId}:${this.authService.sessionVersion}`), distinctUntilChanged(),
    ).subscribe(() => this.scheduleRecovery()));
    this.subscriptions.add(this.online.reconnected$.subscribe(() => this.scheduleRecovery()));
    this.subscriptions.add(this.appState.appStateChange$.pipe(
      distinctUntilChanged(), skip(1), filter(active => active),
    ).subscribe(() => this.scheduleRecovery()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroyed$.next();
    this.destroyed$.complete();
    this.subscriptions.unsubscribe();
    if (this.platform.isBrowser && !this.platform.isDevice) window.removeEventListener('pagehide', this.pageHide);
  }

  public finish(orderId: number, hadEstablishedMedia: boolean, expectedUserId: number | null | undefined, attemptId: string | null = null): Promise<boolean> {
    const userId = this.authService.parsedToken?.userId;
    if (this.destroyed || !userId || userId !== expectedUserId || !Number.isSafeInteger(orderId) || orderId <= 0) return Promise.resolve(false);
    if (attemptId !== null && !isAudioCallAttemptId(attemptId)) return Promise.resolve(false);
    if (attemptId) {
      const [key, entry] = this.retain({orderId, userId, attemptId});
      return this.finishRetained(key, entry);
    }
    const version = this.authService.sessionVersion;
    const key = `${version}:${userId}:${orderId}:legacy-${hadEstablishedMedia}`;
    const existing = this.pending.get(key);
    if (existing) return existing;
    const task = this.finalize(orderId, userId, hadEstablishedMedia, version)
      .finally((): void => { this.pending.delete(key); });
    this.pending.set(key, task);
    return task;
  }

  public cancelStart(toUserId: number, expectedUserId: number, attemptId: string): Promise<boolean> {
    if (this.destroyed || !Number.isSafeInteger(expectedUserId) || expectedUserId <= 0 || !Number.isSafeInteger(toUserId)
      || toUserId <= 0 || toUserId === expectedUserId || !isAudioCallAttemptId(attemptId)) return Promise.resolve(false);
    // Logout may abort create before its catch runs. Retain the original actor, never the new login.
    const [key, entry] = this.retain({toUserId, userId: expectedUserId, attemptId});
    return this.finishRetained(key, entry);
  }

  private exitIntentKey(intent: CallTerminationIntent): string {
    const identity = 'orderId' in intent ? `${intent.userId}.${intent.orderId}` : `start.${intent.userId}.${intent.toUserId}`;
    return `${AudioCallFinalizationService.EXIT_INTENT_PREFIX}${identity}.${intent.attemptId}`;
  }

  private retain(intent: CallTerminationIntent): [string, RetainedCallExit] {
    const key = this.exitIntentKey(intent);
    for (const [storedKey, entry] of this.retained) {
      if (this.exitIntentKey(entry.intent) === key) return [storedKey, entry];
    }
    const entry: RetainedCallExit = {intent, raw: JSON.stringify(intent), batches: 0, restored: false, storageWrite: Promise.resolve()};
    this.retained.set(key, entry);
    this.persist(key, entry);
    return [key, entry];
  }

  private readExitIntents(): void {
    try {
      const storage = window.sessionStorage;
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (!key?.startsWith(AudioCallFinalizationService.EXIT_INTENT_PREFIX)) continue;
        const raw = storage.getItem(key);
        if (!raw) continue;
        this.restoreEntry(key, raw);
      }
    } catch {
      // A browser that denies storage cannot recover the old document's intent.
    }
  }

  private async restoreNativeIntents(): Promise<void> {
    try {
      const {keys} = await Preferences.keys();
      for (const key of keys) {
        if (this.destroyed) return;
        if (!key.startsWith(AudioCallFinalizationService.EXIT_INTENT_PREFIX)) continue;
        const {value} = await Preferences.get({key});
        if (value) this.restoreEntry(key, value);
      }
    } catch {
      // In-memory finalization must still work when native persistence is unavailable.
    }
    this.scheduleRecovery();
  }

  private restoreEntry(key: string, raw: string): void {
    if (this.destroyed || this.retained.has(key)) return;
    try {
      const value: unknown = JSON.parse(raw);
      if (!value || typeof value !== 'object') return;
      const orderId: unknown = Reflect.get(value, 'orderId');
      const toUserId: unknown = Reflect.get(value, 'toUserId');
      const userId: unknown = Reflect.get(value, 'userId');
      const attemptId: unknown = Reflect.get(value, 'attemptId');
      const rejected: unknown = Reflect.get(value, 'rejected');
      const retryAfter: unknown = Reflect.get(value, 'retryAfter');
      if (typeof userId !== 'number' || !Number.isSafeInteger(userId) || userId <= 0
        || typeof attemptId !== 'string' || !isAudioCallAttemptId(attemptId)
        || (rejected !== undefined && rejected !== true)
        || (retryAfter !== undefined && (typeof retryAfter !== 'number' || !Number.isFinite(retryAfter)))) return;
      let intent: CallTerminationIntent;
      if (typeof orderId === 'number' && Number.isSafeInteger(orderId) && orderId > 0 && toUserId === undefined) {
        intent = {orderId, userId, attemptId};
      } else if (orderId === undefined && typeof toUserId === 'number' && Number.isSafeInteger(toUserId)
        && toUserId > 0 && toUserId !== userId) {
        intent = {toUserId, userId, attemptId};
      } else return;
      if (rejected === true) intent.rejected = true;
      if (typeof retryAfter === 'number') intent.retryAfter = retryAfter;
      if (key !== this.exitIntentKey(intent)
        && !('orderId' in intent && key === `${AudioCallFinalizationService.EXIT_INTENT_PREFIX}${intent.orderId}`)) return;
      this.retained.set(key, {raw, intent, batches: 0, restored: true, storageWrite: Promise.resolve()});
    } catch {
      // A malformed entry must not suppress recovery of the other calls.
    }
  }

  private persist(key: string, entry: RetainedCallExit, previousRaw?: string): void {
    if (!this.platform.isBrowser) return;
    const raw = entry.raw;
    if (this.platform.isDevice) {
      entry.storageWrite = entry.storageWrite.then(async () => {
        if (previousRaw !== undefined && (await Preferences.get({key})).value !== previousRaw) return;
        await Preferences.set({key, value: raw});
      }).catch(() => undefined);
    } else {
      try {
        if (previousRaw !== undefined && window.sessionStorage.getItem(key) !== previousRaw) return;
        window.sessionStorage.setItem(key, raw);
      } catch {
        // The in-memory intent remains recoverable without blocking UI/media teardown.
      }
    }
  }

  private async removeConfirmed(key: string, entry: RetainedCallExit): Promise<void> {
    if (this.retained.get(key) !== entry) return;
    this.retained.delete(key);
    if (!this.platform.isBrowser) return;
    const raw = entry.raw;
    if (this.platform.isDevice) {
      // Keep the remove behind the write even when the native bridge responds late.
      entry.storageWrite = entry.storageWrite.then(async () => {
        if ((await Preferences.get({key})).value === raw) await Preferences.remove({key});
      }).catch(() => undefined);
    } else {
      try {
        if (window.sessionStorage.getItem(key) === raw) window.sessionStorage.removeItem(key);
      } catch {
        // A repeated, confirmed finish is idempotent if storage cannot be removed.
      }
    }
  }

  private scheduleRecovery(): void {
    if (this.destroyed || this.recoveryScheduled) return;
    this.recoveryScheduled = true;
    void Promise.resolve().then(async () => {
      this.recoveryScheduled = false;
      if (this.recoveryRunning) { this.recoveryRequested = true; return; }
      this.recoveryRunning = true;
      try {
        do {
          this.recoveryRequested = false;
          for (const [key, entry] of this.retained) {
            if (this.destroyed || this.online.isOffline) break;
            if (entry.intent.userId !== this.authService.parsedToken?.userId) continue;
            if (entry.restored) {
              entry.restored = false;
              const selected = this.session.selectedOrder;
              if ('orderId' in entry.intent && selected?.id === entry.intent.orderId && selected.myUserId === entry.intent.userId
                && !this.session.getAttempt(selected.id, selected.myUserId)) this.session.clearSelectedOrder();
            }
            await this.finishRetained(key, entry);
          }
        } while (this.recoveryRequested && !this.destroyed);
      } finally {
        this.recoveryRunning = false;
      }
    });
  }

  private finishRetained(key: string, entry: RetainedCallExit): Promise<boolean> {
    const version = this.authService.sessionVersion;
    const pendingKey = `${version}:${this.exitIntentKey(entry.intent)}`;
    const existing = this.pending.get(pendingKey);
    if (existing) return existing;
    if (!this.ownsSession(entry.intent.userId, version) || entry.intent.rejected
      || entry.batches >= this.maxBatches || entry.authBlockedVersion === version
      || (entry.intent.retryAfter ?? 0) > Date.now()) return Promise.resolve(false);
    entry.batches++;
    const task = this.runRetained(key, entry, version).finally(() => { this.pending.delete(pendingKey); });
    this.pending.set(pendingKey, task);
    return task;
  }

  private async runRetained(key: string, entry: RetainedCallExit, version: number): Promise<boolean> {
    if (this.platform.isDevice) {
      // Persistence starts before HTTP, but a stuck native bridge must not block termination.
      let timer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([entry.storageWrite, new Promise<void>(resolve => { timer = setTimeout(resolve, 1000); })]);
      if (timer !== undefined) clearTimeout(timer);
    }
    const success = 'orderId' in entry.intent
      ? await this.finalizeAttempt(entry.intent, entry, version)
      : await this.finalizeStart(entry.intent, entry, version);
    if (!this.ownsSession(entry.intent.userId, version)) return false;
    if (success) await this.removeConfirmed(key, entry);
    else {
      const previousRaw = entry.raw;
      entry.raw = JSON.stringify(entry.intent);
      if (entry.raw !== previousRaw) this.persist(key, entry, previousRaw);
    }
    return success;
  }

  private ownsSession(userId: number, version: number): boolean {
    return !this.destroyed && this.authService.parsedToken?.userId === userId && this.authService.sessionVersion === version;
  }

  private request<T>(factory: () => Observable<T>, userId: number, version: number, duration = this.requestTimeoutMs): Promise<T> {
    return firstValueFrom(defer(factory).pipe(timeout(duration), takeUntil(merge(
      this.destroyed$, this.authService.parsedToken$.pipe(filter(() => !this.ownsSession(userId, version))),
    ))));
  }

  private async finalizeStart(intent: CallStartCancelIntent, entry: RetainedCallExit, version: number): Promise<boolean> {
    const {toUserId, userId, attemptId} = intent;
    const startedAt = Date.now();
    const body = new AudioCallStartCancelRequest({to_id: toUserId, call_actor_id: userId, call_attempt_id: attemptId});
    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      if (!this.ownsSession(userId, version)) return false;
      try {
        await this.request(() => this.client.avatarOrderCancelStart(body), userId, version);
        if (!this.ownsSession(userId, version)) return false;
        this.report(null, startedAt, attempt, true);
        return true;
      } catch (error) {
        if (!this.ownsSession(userId, version)) return false;
        if (!this.retryableFailure(error, entry, version)) {
          this.report(null, startedAt, attempt, false);
          return false;
        }
      }
      if (attempt < this.maxAttempts) await new Promise<void>(resolve => { setTimeout(resolve, attempt * 500); });
    }
    this.report(null, startedAt, this.maxAttempts, false);
    return false;
  }

  private async finalizeAttempt(intent: CallExitIntent, entry: RetainedCallExit, version: number): Promise<boolean> {
    const {orderId, userId, attemptId} = intent;
    const startedAt = Date.now();
    const body = new AudioCallAttemptFinishRequest({call_attempt_id: attemptId, call_actor_id: userId});
    for (let attempt = 1; attempt <= this.maxAttempts; attempt += 1) {
      if (!this.ownsSession(userId, version)) return false;
      try {
        const response = await this.request(() => this.client.avatarOrderFinishAttempt(orderId, body), userId, version);
        if (!this.ownsSession(userId, version)) return false;
        const success = this.publishConfirmedTerminal(response, orderId, userId);
        this.report(orderId, startedAt, attempt, success);
        return success;
      } catch (error) {
        if (!this.ownsSession(userId, version)) return false;
        // Never bypass an ownership conflict with the legacy done/exit path.
        if (!this.retryableFailure(error, entry, version)) {
          this.report(orderId, startedAt, attempt, false);
          return false;
        }
      }
      if (attempt < this.maxAttempts) {
        await new Promise<void>(resolve => { setTimeout(resolve, attempt * 500); });
      }
    }
    let confirmed = false;
    try {
      if (!this.ownsSession(userId, version)) return false;
      const response = await this.request(() => this.client.avatarOrderGet(orderId), userId, version, 2000);
      if (!this.ownsSession(userId, version)) return false;
      confirmed = this.publishConfirmedTerminal(response, orderId, userId);
    } catch {
      // The final POST response may be lost after commit; only terminal SQL is proof.
    }
    if (!this.ownsSession(userId, version)) return false;
    this.report(orderId, startedAt, this.maxAttempts, confirmed);
    return confirmed;
  }

  private retryableFailure(error: unknown, entry: RetainedCallExit, version: number): boolean {
    if (!error || typeof error !== 'object') return true;
    const status: unknown = Reflect.get(error, 'status');
    if (typeof status !== 'number' || [0, 500, 502, 503, 504].includes(status)) return true;
    if (status === 401) entry.authBlockedVersion = version;
    else if (status === 429) entry.intent.retryAfter = this.retryAfter(error);
    else if ([400, 403, 404, 409, 410, 422].includes(status)) entry.intent.rejected = true;
    return false;
  }

  private retryAfter(error: object): number {
    const headers: unknown = Reflect.get(error, 'headers');
    const value: unknown = headers && typeof headers === 'object' ? Reflect.get(headers, 'retry-after') : undefined;
    const seconds = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN;
    const date = typeof value === 'string' ? Date.parse(value) : NaN;
    const requested = Number.isFinite(seconds) ? Date.now() + seconds * 1000 : date;
    return Math.max(Date.now() + 60000, Number.isFinite(requested) ? requested : 0);
  }

  private publishConfirmedTerminal(response: AvatarOrderDataResponse, orderId: number, userId: number): boolean {
    const order = response?.data;
    if (order?.id !== orderId || order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
      || (order.from_id !== userId && order.to_id !== userId) || !this.isFinal(order.status)) return false;
    this.updates.updateOrder(order);
    return true;
  }

  private async finalize(orderId: number, userId: number, hadEstablishedMedia: boolean, version: number): Promise<boolean> {
    const startedAt = Date.now();
    for (let attempt = 1; attempt <= this.maxAttempts; attempt += 1) {
      if (!this.ownsSession(userId, version)) return false;
      try {
        // Socket ownership can disappear while media is still flowing. SQL is authoritative.
        const current = await this.request(() => this.client.avatarOrderGet(orderId), userId, version);
        if (!this.ownsSession(userId, version)) return false;
        const order = current?.data;
        if (order?.id !== orderId || order.type !== CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL
          || (order.from_id !== userId && order.to_id !== userId)) {
          this.report(orderId, startedAt, attempt, false);
          return false;
        }
        let response: AvatarOrderDataResponse = current;
        if (order.status === CALL_ORDER_STATUS_NUMBERS.ACCEPTED) {
          // A reused accepted order may belong to another device. User auth alone is not ownership.
          if (!hadEstablishedMedia) {
            this.report(orderId, startedAt, attempt, false);
            return false;
          }
          response = await this.request(() => this.client.avatarOrderDone(orderId), userId, version);
        } else if (order.status === CALL_ORDER_STATUS_NUMBERS.CREATED) {
          const request = order.from_id === userId
            ? this.client.avatarOrderCancel(orderId) : this.client.avatarOrderReject(orderId);
          response = await this.request(() => request, userId, version);
        }
        if (!this.ownsSession(userId, version)) return false;
        if (response?.data?.id === orderId && this.isFinal(response.data.status)) {
          this.updates.updateOrder(response.data);
          this.report(orderId, startedAt, attempt, true);
          return true;
        }
      } catch {
        // A concurrent accept/exit or a lost HTTP response requires a fresh read, not blind cancel.
        if (!this.ownsSession(userId, version)) return false;
      }
      if (attempt < this.maxAttempts) {
        await new Promise<void>((resolve): void => { setTimeout(resolve, attempt * 500); });
      }
    }
    this.report(orderId, startedAt, this.maxAttempts, false);
    return false;
  }

  private isFinal(status: number | undefined): boolean {
    return status === CALL_ORDER_STATUS_NUMBERS.COMPLETED
      || status === CALL_ORDER_STATUS_NUMBERS.CANCELED
      || status === CALL_ORDER_STATUS_NUMBERS.REJECTED;
  }

  private report(orderId: number | null, startedAt: number, attempt: number, success: boolean): void {
    try {
      this.telemetry.record({
        order_id: orderId, session_id: `finalize-${orderId}-${startedAt}`, seq: 1,
        timestamp: Date.now(), elapsed_ms: Date.now() - startedAt, role: null,
        source: 'http', stage: orderId === null
          ? (success ? 'order.start_canceled' : 'order.start_cancel_failed')
          : (success ? 'order.finalized' : 'order.finalize_failed'),
        tone: success ? 'success' : 'error',
      }, {attempt, maxAttempts: this.maxAttempts});
    } catch {
      // Diagnostics must not cause an already completed operation to retry.
    }
  }
}
